const state = () => ({
    save: {
        data: {}
    },
    ownNotes: [],
    sharedNotes: [],
    filterData: undefined,
    loading: false,
})

const getters = {
    showData: (state) => state.save.data,
}

const actions = {
    async get({state, commit}, {filterData} = {}) {
        commit('setLoading', true)

        state.filterData = filterData

        let params = {
            page_limit: 9999,
            ...this._handler.getContextParams(),
            sort: 'updated_at:desc',
        }

        if (state.filterData) {
            params = {...params, ...state.filterData}
        }

        try {
            const res = await this._vm.axios.get(this._vm.endpoints['module_note'], {
                params: params
            })

            const {data} = res.data
            commit('setOwnNotes', data.own_notes || [])
            commit('setSharedNotes', data.shared_notes || [])
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        } finally {
            commit('setLoading', false)
        }
    },

    async show({commit}, {id, with_trashed} = {}) {
        commit('setLoading', true)
        commit('updateSaveData', {})

        try {
            const params = with_trashed ? {with_trashed: 1} : {}
            const config = {params}
            if (with_trashed) config._ignoreErrors = ['record_not_found']
            const res = await this._vm.axios.get(this._vm.endpoints['module_note'] + '/' + id, config)
            const {data} = res.data
            commit('updateSaveData', data)
            return data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        } finally {
            commit('setLoading', false)
        }
    },

    async add({commit}, {formData}) {
        commit('setLoading', true)

        try {
            const payload = {
                content: formData.content,
                color: formData.color || null,
                category_id: formData.category_id || null,
                ...this._handler.getContextParams(),
                visibility_id: formData.visibility_id || 1,
                edit_permission_id: formData.edit_permission_id || 1,
                viewer_ids: formData.viewer_ids || [],
                editor_ids: formData.editor_ids || [],
                tag_ids: formData.tag_ids || [],
            }

            const res = await this._vm.axios.post(this._vm.endpoints['module_note'], payload)
            return res.data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        } finally {
            commit('setLoading', false)
        }
    },

    async update({commit}, {id, formData}) {
        commit('setLoading', true)

        try {
            const payload = {
                content: formData.content,
                color: formData.color || null,
                category_id: formData.category_id || null,
                ...this._handler.getContextParams(),
                visibility_id: formData.visibility_id || 1,
                edit_permission_id: formData.edit_permission_id || 1,
                viewer_ids: formData.viewer_ids || [],
                editor_ids: formData.editor_ids || [],
                tag_ids: formData.tag_ids || [],
            }

            const res = await this._vm.axios.put(this._vm.endpoints['module_note'] + '/' + id, payload)
            return res.data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        } finally {
            commit('setLoading', false)
        }
    },

    async updateColor({commit}, {id, color}) {
        try {
            const res = await this._vm.axios.put(this._vm.endpoints['module_note'] + '/' + id, {
                color: color,
            })
            return res.data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        }
    },

    async moveToCategory({commit}, {id, category_id}) {
        try {
            const res = await this._vm.axios.put(this._vm.endpoints['module_note'] + '/' + id, {
                category_id: category_id,
            })
            return res.data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        }
    },

    async delete({commit, dispatch}, {id}) {
        try {
            const res = await this._vm.axios.delete(this._vm.endpoints['module_note'] + '/' + id, {
                _ignoreErrors: ['record_not_found']
            })
            return res.data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        }
    },

    async batchDelete({commit}, {ids}) {
        try {
            const res = await this._vm.axios.delete(
                this._vm.endpoints['module_note'] + '/batch/delete',
                { data: { ids } }
            )
            return res.data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        }
    },

    async batchMove({commit}, {ids, category_id}) {
        try {
            const res = await this._vm.axios.put(
                this._vm.endpoints['module_note'] + '/batch/move',
                { ids, category_id }
            )
            return res.data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        }
    },

    async pin({commit}, {noteId}) {
        try {
            const res = await this._vm.axios.post(this._vm.endpoints['module_note_pin'], {
                note_id: noteId,
            })
            return res.data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        }
    },

    async unpin({commit}, {noteId}) {
        try {
            const res = await this._vm.axios.delete(this._vm.endpoints['module_note_pin'] + '/' + noteId)
            return res.data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        }
    },

    async setReminder({commit}, {noteId, event_at, reminder_at}) {
        try {
            const res = await this._vm.axios.post(this._vm.endpoints['module_note_reminder'], {
                note_id: noteId,
                event_at,
                reminder_at: reminder_at || null,
            })
            return res.data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        }
    },

    async deleteReminder({commit}, {noteId}) {
        try {
            const res = await this._vm.axios.delete(this._vm.endpoints['module_note_reminder'] + '/' + noteId)
            return res.data
        } catch (error) {
            this._vm.onResponseFailure(error.response?.data)
        }
    },
}

const mutations = {
    setLoading(state, val) {
        state.loading = val
    },
    setOwnNotes(state, data) {
        state.ownNotes = data
    },
    setSharedNotes(state, data) {
        state.sharedNotes = data
    },
    updateSaveData(state, data) {
        state.save.data = data
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
